import { uniqBy } from 'lodash';
import Group from './group';
import User from './user';
import { EVENT_USER_STATUS_DATA, MEMBER_ENTRY_TYPE, MEMBER_ENTRY_TYPE_DATA, MEMBER_JOIN_METHOD, SUBORDINATION_TRANSFER_REQUEST_TYPE, SUBORDINATION_TRANSFER_STATUS, SUBORDINATION_TRANSFER_STATUS_DATA } from '@/modules/Events/constants';

// NOTE: СМОТРИМ СТАТУС ПО ЗАЯВКЕ (ПОТОМ ПЕРЕДЕЛАТЬ)
const getStatus = (participant, nominationId = null) => {
  if (participant.status) {
    return EVENT_USER_STATUS_DATA[participant.status];
  }
  if (participant.request) {
    const nomination_id = nominationId || participant?.nomination_id;
    const nominationRequest = !!nomination_id && (participant.request || []).find((i) => i.nomination_id === nomination_id);
    const [firstRequest] = participant.request || [];
    const request = nominationRequest || firstRequest;
    if (request?.status) {
      return SUBORDINATION_TRANSFER_STATUS_DATA[request.status];
    }
  }

  return null;
};

export default class Participant extends User {
  constructor(participant, url) {
    super(participant, url);

    this.certificate = {
      certificate_number: '-',
      ...(participant.certificate || {}),
    };
    this.directory_attributes = {
      grade: '-', // Тут оно не так работает, у нас @directory_attributes - это массив
      ...(participant.directory_attributes || {}),
    };
    this.requests = [...(participant.request || [])];
    this.status = getStatus(participant);
    [this.request] = participant.nomination_id && participant.status
      ? this.requests.filter(({ nomination_id }) => nomination_id === participant.nomination_id)
      : this.requests;
    this.organizer = participant.organizer || null;
    this.subordination_id = participant.subordination_id || null;
    this.prize_places = participant.prize_places || null;
    this.subordinations = participant.subordinations || [];
    this.main_subordinations = participant.main_subordinations || [];
    this.user_requests = participant.user_requests || [];
    this.transferActions = [];
    this.is_imported = participant.is_imported || false;
    this.import_ids = participant.import_ids || [];

    this.join_method = participant.join_method ?? MEMBER_JOIN_METHOD.request;

    this.nomination_id = participant.nomination_id || null;
    this.group_id = participant.group_id || null;
    this.event_groups = [participant.event_groups].flat().filter(Boolean).map((group) => new Group(group));
    this.groups = [participant.groups].flat().filter(Boolean).map((group) => new Group(group));

    this.subordinate_id = participant.subordinate_id || null;

    this.mainSubordinationTransferData = null; // { event, nomination, group }

    this.joined_at = participant.joined_at;

    this.can_delete = participant.can_delete || [];
    this.tournament_groups = participant.tournament_groups || [];
  }

  get canDeleteByNomination() {
    const { can_delete, nomination_id } = this;
    return can_delete.find((i) => i.nomination_id === nomination_id)?.can_delete ?? true;
  }

  setStatus(...args) {
    this.status = getStatus(this.response, ...args);
  }

  static fromSubordinated(member, url) {
    const participant = new Participant(member, url);
    participant.setSubordinateMemberContext(member);
    return participant;
  }

  setSubordinateMemberContext(member) {
    const request = member.event_subordination_request || null;
    const { nomination_id, event_subordination_id, event_subordination_group_id, event_subordination_status, tournament_id } = member;
    this.event_subordination_request = request;
    this.name = this.name || member.user_name || '';
    this.nomination_id = request?.nomination_id ?? nomination_id;
    this.group_id = request ? request.group_id : event_subordination_group_id;
    this.subordination_id = event_subordination_id;
    this.subordinate_id = event_subordination_id;
    this.request = request;
    this.requests = request ? [request] : [];
    this.reason = request?.reason || null;
    this.main_subordinations = [{
      ...request,
      nomination_id: this.nomination_id,
      group_id: this.group_id,
      status: request?.status ?? event_subordination_status,
      subordination_id: event_subordination_id,
      tournament_id,
    }];
    this.status = SUBORDINATION_TRANSFER_STATUS_DATA[this.mainSubordination.status] || null;
  }

  initTransferActions(actions) {
    this.transferActions = actions || [];
  }

  setCategoryGroup({ nominationId, group, previousGroupId }) {
    const nextGroup = new Group({
      ...group.response,
      id: group.id,
      name: group.name,
      nomination_id: nominationId,
    }, group.url);
    const replaceGroup = (groups) => [
      ...(groups || []).filter((current) => current.nomination_id !== nominationId
        && current.id !== previousGroupId && current.id !== nextGroup.id),
      nextGroup,
    ];

    this.groups = replaceGroup(this.groups);
    this.event_groups = replaceGroup(this.event_groups);
    if (!this.nomination_id || this.nomination_id === nominationId) {
      this.group_id = nextGroup.id;
    }
  }

  get currentSubordinations() {
    const { main_subordinations, nomination_id } = this;
    if (!nomination_id) return main_subordinations;
    return main_subordinations.filter(({ nomination_id: nom }) => nomination_id === nom);
  }

  get mainSubordination() {
    const [mainSubordination] = this.currentSubordinations;
    return mainSubordination;
  }

  get isAwaitingSubordinateConfirmation() {
    const { subordinate_id, mainSubordination } = this;
    return !!subordinate_id
      && mainSubordination?.status === SUBORDINATION_TRANSFER_STATUS.new
      && mainSubordination?.request_type === SUBORDINATION_TRANSFER_REQUEST_TYPE.pullRequest;
  }

  get mainSubordinationTransferDataText() {
    return this.mainSubordinationTransferData?.event?.organization?.displayName || '';
  }

  get importData() {
    const { import_ids, nomination_id } = this;
    return import_ids.find((i) => i?.nomination_id === nomination_id);
  }

  get import_id() {
    return this.importData?.id;
  }

  get isImported() {
    const { is_imported, join_method } = this;
    return !!is_imported; // || (join_method === MEMBER_JOIN_METHOD.imported);
  }

  get isSubordinated() {
    const { mainSubordination, join_method } = this;
    return !!mainSubordination || (join_method === MEMBER_JOIN_METHOD.subordinated);
  }

  get entryType() {
    const { registered, imported } = MEMBER_ENTRY_TYPE;
    return this.is_imported ? imported : registered;
  }

  get entryTypeData() {
    return MEMBER_ENTRY_TYPE_DATA[this.entryType];
  }

  get groupName() {
    const { groups, event_groups, nomination_id: nomination } = this;
    const list = uniqBy([...groups, ...event_groups], 'id').filter(({ nomination_id }) => !nomination || nomination === nomination_id);
    if (!list?.length) return '';
    const withoutBase = list.filter(({ isBase }) => !isBase);
    return (withoutBase?.length ? withoutBase : list).map(({ name }) => name).join(', ');
  }
}
